// src/rms/utils/safetySignposting.js
//
// Safety signposting for RMS referral forms (RMS-314).
//
// When someone answers a safety question in a way that indicates risk, they
// are shown the safety information on screen at the moment they answer, and
// the same information is emailed to them. This mirrors the assessment
// platform's SLaM crisis-support pattern (SelfHarmCrisisSupportDialog +
// self_harm_patient_support email), agreed as the model on 14 Sep 2026.
//
// The provider-side alert is a separate, already-shipped path
// (_maybe_send_urgent_risk_alerts in rms_functions/rms_forms.py); this module
// only covers what the person filling in the form sees.
//
// CONTENT OWNERSHIP: the services below are Blackpool / Lancashire specific,
// supplied by Leanne Havlin (Blackpool Teaching Hospitals, 10 Sep 2026) for
// the Lancashire RMS. They are correct for the only organisation currently
// using RMS. Before a second organisation is onboarded this must move to
// per-organisation configuration — signposting a child to another area's
// crisis team is a patient-safety problem, not a cosmetic one.
// The emergency block above it is true everywhere and is shown regardless.

/**
 * The answers that trigger safety signposting, listed per question.
 *
 * Every option except the plain "no" one: "No" on the self-harm questions and
 * "None of the above" on risk behaviours. "Don't know / Not sure" counts —
 * uncertainty about whether a child has harmed themselves is not reassurance.
 * Specified by Jasmine Murphy on 7 Sep 2026 and confirmed for the risk
 * behaviours question on 16 Sep 2026.
 *
 * Listed explicitly rather than derived from the schema, matching the
 * assessment platform's CONCERNING_ANSWERS constant, and deliberately the same
 * answers as _URGENT_RISK_ALERT_CONFIG in rms_functions/rms_forms.py — whenever
 * the service is alerted about a disclosure the person who made it is given
 * help, so the two must not drift apart. A test asserts they match.
 */
export const SAFETY_TRIGGER_OPTIONS = {
  self_harm_history: ['yes_recently', 'yes_past', 'thoughts_no_plan', 'not_sure'],
  lg_self_harm_history: ['yes_recently', 'yes_past', 'thoughts_no_plan', 'not_sure'],
  risk_behaviours: [
    'self_harm',
    'suicidal_thoughts',
    'suicide_plans',
    'suicide_attempt',
    'running_away',
    'social_withdrawal',
    'sexualised_behaviour',
    'not_sure',
  ],
};

/** The questions carrying safety signposting. */
export const SAFETY_TRIGGER_QUESTION_IDS = Object.keys(SAFETY_TRIGGER_OPTIONS);

/** Title shown above the safety information, on screen and in the email. */
export const SAFETY_TITLE = 'Important safety information';

/**
 * Opening line, by who is filling in the form. Follows the assessment
 * platform's crisis-support email: thank them, say plainly why they are
 * seeing this, and do not imply anyone is reading the form right now.
 */
export const SAFETY_OPENING = {
  school_user: (childName) =>
    `Thank you for taking the time to complete the referral form for ${childName}. One of your answers tells us ${childName} may need mental health support.`,
  guardian: (childName) =>
    `Thank you for taking the time to complete the referral form for ${childName}. One of your answers tells us ${childName} may need mental health support.`,
};

/**
 * Closing line, mirroring the assessment platform's "The 3 Boroughs clinics are
 * a non-urgent outpatients service." The referral is not a route to urgent care
 * and must not be mistaken for one.
 */
export const SAFETY_NOT_URGENT_SERVICE =
  'The referral service is not an urgent service.';

/**
 * The line that must appear first, wherever this content is shown: the form is
 * not a route to urgent help and no one is watching it in real time.
 */
export const SAFETY_NOT_MONITORED =
  'This referral form is not monitored for urgent or emergency concerns and must not be used to get immediate help.';

/**
 * What to do right now, by who is filling in the form. A teacher has an
 * organisational safeguarding route; a parent does not, so they are told
 * plainly where to go.
 */
export const SAFETY_IMMEDIATE_ACTION = {
  school_user: (childName) =>
    `If you think ${childName} is in immediate danger, needs urgent medical help, or may hurt themselves or someone else, follow your organisation's safeguarding and emergency procedures.`,
  guardian: (childName) =>
    `If you think ${childName} needs medical help right away, or may hurt themselves or someone else, call 999 or take them to the A&E department at Blackpool Victoria Hospital.`,
};

/** True anywhere in England, shown to everyone. */
export const EMERGENCY_SERVICES = [
  {
    name: 'Emergency services',
    contact: '999',
    detail: 'In any medical or mental health emergency, or if someone is in immediate danger.',
  },
  {
    name: 'NHS 111',
    contact: '111',
    detail: "Select the mental health option. 111 will tell you the right place to get help, and you may be able to speak to a mental health practitioner.",
  },
  {
    name: 'Childline',
    contact: '0800 1111',
    detail: 'Free, confidential support for children and young people.',
  },
];

/**
 * Blackpool / Lancashire services (Leanne Havlin, 10 Sep 2026).
 * Most sit behind one Single Point of Access number on different options.
 */
export const LOCAL_SERVICES = [
  {
    name: 'Single Point of Access',
    contact: '0800 121 7762, option 0',
    detail:
      'Option 1 CAMHS, option 2 Blackpool RAIS, option 3 YoutherapY, option 4 Children and Young People’s Primary Mental Health Service, option 5 Learning Disability and Neurodevelopmental. Self-referral at blackpoolteachinghospitals.nhs.uk/services/childrens-and-young-peoples-mh-services.',
  },
  {
    name: 'CAMHS',
    contact: '0800 121 7762, option 1',
    detail:
      'Monday to Friday, 9am to 5pm. Ask for your CAMHS care coordinator, a member of the team, or the duty team. If no one is free a message is taken and your call returned.',
  },
  {
    name: 'Blackpool RAIS',
    contact: '0800 121 7762, option 2',
    detail:
      'Urgent advice and support with concerns about safety. Out of hours: weekday evenings 5pm to 10pm and weekends 10am to 10pm. They also see young people who come to A&E in a mental health crisis, and clinics can be booked. All routes accepted, including self-referral. Also by email at bfwh.rais.team@nhs.net.',
  },
  {
    name: 'Initial Response Service',
    contact: '0800 013 0708',
    detail: 'Crisis response and guidance, 24 hours a day, all ages.',
  },
  {
    name: 'YoutherapY',
    contact: '0800 121 7762, option 3',
    detail:
      'Counselling drop-in for ages 11 to 25, Wednesdays 3pm to 5pm at 26 Talbot Road, Blackpool, FY1 1LF.',
  },
  {
    name: 'Children and Young People’s Primary Mental Health Service',
    contact: '0800 121 7762, option 4',
    detail:
      'Primary Mental Health Worker drop-in Mondays 2pm to 4:30pm at Revoe Children’s Centre, Grasmere Road, Blackpool, FY1 5HP. SHINE Mental Health Support Team offers school-based support. Other drop-ins at fyidirectory.co.uk/health/childrens-health/primary-mental-health-worker.',
  },
  {
    name: 'Learning Disability and Neurodevelopmental',
    contact: '0800 121 7762, option 5',
    detail: 'Monday to Friday, 9am to 5pm.',
  },
  {
    name: 'Children’s Social Care',
    contact: '01253 477299',
    detail: 'Duty team, 24 hours a day.',
  },
  {
    name: 'Barnardo’s Thrive',
    contact: '01772 505138',
    detail:
      'Therapeutic and counselling support for ages 5 to 18, extending to 25 based on SEN need, across Lancashire and South Cumbria. Self-referral at barnardos.org.uk/get-support/services/lancashire-and-south-cumbria-thrive-service, or thrivelsc@barnardos.org.uk.',
  },
  {
    name: 'FYI Directory',
    contact: 'fyidirectory.co.uk',
    detail:
      'Health, social care and community directory for the Fylde Coast — search for organisations that can help.',
  },
];

/** Self-help resources, listed as plain addresses so they read the same in email and on screen. */
export const SELF_HELP_LINKS = [
  'camhs-resources.co.uk',
  'youngminds.org.uk',
  'healthyyoungmindslsc.co.uk',
  'kooth.com',
];

/**
 * Does this answer to this question warrant showing the safety information?
 *
 * Reads only the answer and the list above — never the schema — so the trigger
 * cannot change under us when a schema version is republished.
 *
 * @param {object} question - the schema question (needs questionId)
 * @param {*} answer - the stored answer: option id, {optionId}, or an array of either
 * @returns {boolean}
 */
export function answerTriggersSafetyInfo(question, answer) {
  const questionId = question?.questionId;
  const triggers = questionId ? SAFETY_TRIGGER_OPTIONS[questionId] : null;
  if (!triggers) return false;
  if (answer === undefined || answer === null || answer === '') return false;

  const selectedIds = (Array.isArray(answer) ? answer : [answer])
    .map((item) => (item && typeof item === 'object' ? item.optionId : item))
    .filter(Boolean);

  return selectedIds.some((id) => triggers.includes(id));
}

/**
 * The first triggering question on a step, or null.
 *
 * RmsFormTemplate flattens every schema question into its own wizard step
 * (`{...question, sectionTitle}`), so the step passed to `beforeNext` IS a
 * single question carrying `questionId` directly — there is no `questions`
 * array on it. A grouped step (several questions under `questions`) is also
 * accepted for section-shaped callers.
 *
 * @param {object} step - the current form step: a flattened single question,
 *   or a group carrying `questions`
 * @param {object} answers - all answers for the form
 * @returns {{questionId: string}|null}
 */
export function findSafetyTrigger(step, answers) {
  const questions = Array.isArray(step?.questions)
    ? step.questions
    : step?.questionId
      ? [step]
      : [];
  for (const question of questions) {
    if (answerTriggersSafetyInfo(question, (answers || {})[question.questionId])) {
      return { questionId: question.questionId };
    }
  }
  return null;
}
